import { IMeta, IPagination, ISort } from "@delanobgt/admin-core/util/types";
import { produce } from "immer";
import { Reducer } from "redux";
import {
  EPackageSetSortField,
  IPackageSet,
  PPackageSetFilter,
} from "src/core/packageSet/entities";
import {
  ActionType,
  createAction,
  createAsyncAction,
  getType,
} from "typesafe-actions";

/*
 * state
 */
export interface IPackageSetState {
  packageSets: IPackageSet[];
  loading: boolean;
  error: unknown;
  meta: IMeta;
  filter: PPackageSetFilter;
  pagination: IPagination;
  sorts: ISort<EPackageSetSortField>[];
  refreshDelay: number;
}

const INITIAL_STATE: IPackageSetState = {
  packageSets: [],
  loading: false,
  error: null,
  meta: {
    limit: 10,
    offset: 0,
    total: 0,
  },
  filter: {},
  pagination: {
    limit: 10,
    offset: 0,
  },
  sorts: [],
  refreshDelay: 5000,
};

/*
 * action types
 */
const FETCH_REQUESTED = "package-set/FETCH_REQUESTED";
const FETCH_SUCCEEDED = "package-set/FETCH_SUCCEEDED";
const FETCH_FAILED = "package-set/FETCH_FAILED";
const SET_FILTER_PAGINATION_SORT = "package-set/SET_FILTER_PAGINATION_SORT";
const SET_REFRESH_DELAY = "package-set/SET_REFRESH_DELAY";
const POLLING_START_REQUESTED = "package-set/POLLING_START_REQUESTED";
const POLLING_STOP_REQUESTED = "package-set/POLLING_STOP_REQUESTED";

/*
 * action creators
 */
export const actions = {
  setFilterPaginationSort: createAction(SET_FILTER_PAGINATION_SORT)<{
    filter: PPackageSetFilter;
    pagination: IPagination;
    sorts: ISort<EPackageSetSortField>[];
  }>(),

  setRefreshDelay: createAction(SET_REFRESH_DELAY)<{
    refreshDelay: number;
  }>(),

  fetchAsync: createAsyncAction(FETCH_REQUESTED, FETCH_SUCCEEDED, FETCH_FAILED)<
    unknown,
    {
      data: IPackageSet[];
      meta: IMeta;
    },
    unknown
  >(),

  pollingStartRequested: createAction(POLLING_START_REQUESTED)(),

  pollingStopRequested: createAction(POLLING_STOP_REQUESTED)(),
};
export type TActions = ActionType<typeof actions>;

const reducer: Reducer<IPackageSetState, TActions> = (
  state = INITIAL_STATE,
  action
) => {
  return produce<IPackageSetState>(state, (draft) => {
    switch (action.type) {
      case getType(actions.setFilterPaginationSort): {
        const { payload } = action;
        draft.filter = payload.filter;
        draft.pagination = payload.pagination;
        draft.sorts = payload.sorts;
        break;
      }
      case getType(actions.setRefreshDelay): {
        const { payload } = action;
        draft.refreshDelay = payload.refreshDelay;
        break;
      }
      case getType(actions.fetchAsync.request): {
        draft.loading = true;
        draft.error = null;
        break;
      }
      case getType(actions.fetchAsync.success): {
        const {
          payload: { data, meta },
        } = action;
        draft.packageSets = data;
        draft.loading = false;
        draft.error = null;
        draft.meta = meta;
        break;
      }
      case getType(actions.fetchAsync.failure): {
        const { payload } = action;
        draft.loading = false;
        draft.error = payload;
        break;
      }
    }
  });
};

export default reducer;
